/**
 * Debounced search over the Deal Room's workbooks, for the loan tape
 * onboarding's pickers (DOP-627).
 *
 * `enabled` gates the request so a closed picker costs nothing. A failed
 * search reads as "no workbooks found": the picker is a convenience over a
 * list, and an error state there would block a user who can simply retype.
 */

import { useEffect, useState } from "react";
import type { CatalogDocument } from "./useDocumentMention";
import { searchLoanTapeWorkbooks } from "../services/loanTapeDashboardService";

const DEBOUNCE_MS = 200;

export interface UseWorkbookSearch {
	results: CatalogDocument[];
	loading: boolean;
}

export function useWorkbookSearch(
	query: string,
	enabled: boolean,
	projectId?: string | null,
): UseWorkbookSearch {
	const [results, setResults] = useState<CatalogDocument[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!enabled) return;
		let cancelled = false;
		const timer = window.setTimeout(async () => {
			setLoading(true);
			try {
				const docs = await searchLoanTapeWorkbooks(query, { projectId });
				if (!cancelled) setResults(docs);
			} catch {
				if (!cancelled) setResults([]);
			} finally {
				if (!cancelled) setLoading(false);
			}
		}, DEBOUNCE_MS);
		return () => {
			cancelled = true;
			window.clearTimeout(timer);
		};
	}, [query, enabled, projectId]);

	return { results, loading };
}
